import { create } from "zustand";
import { devtools } from "zustand/middleware";
import type {
  IncidentFormInput,
  IncidentRecord,
  CaseIndexRecord,
} from "../types";
import { useLookupStore } from "./lookupStore";
import { isoToDateOnly } from "../utils/dateHelpers";

// API client instance - will be injected by components
let apiClient: any = null;

export const setVeterinaryApiClient = (client: any) => {
  apiClient = client;
};

// =============================================================================
// GENERIC LOOKUP RESOLUTION (matches mosquito store pattern)
// =============================================================================

const VETERINARY_MODULE_ID = 2;

const resolveLookup = (
  name: string,
  items: { id: number; text: string }[]
): { id: number; text: string } => {
  if (!name || typeof name !== "string") {
    return items[0] || { id: 1, text: "Unknown" };
  }
  const found = items.find(
    (item) => item.text.toLowerCase() === name.toLowerCase()
  );
  return found || items[0] || { id: 1, text: name || "Unknown" };
};

// Lookup getters
const getEpiStatuses = () =>
  useLookupStore.getState().getEpiStatuses(VETERINARY_MODULE_ID);
const getSpecies = () =>
  useLookupStore.getState().getSpecies(VETERINARY_MODULE_ID);
const getVaccinationStatuses = () =>
  useLookupStore.getState().getVaccinationStatuses(VETERINARY_MODULE_ID);
const getAnimalStatuses = () => useLookupStore.getState().animalStatuses;
const getStates = () => useLookupStore.getState().states;
const getCounties = () => useLookupStore.getState().counties;

// Lookup resolution helpers
const resolveEpiStatusByName = (name: string): { id: number; text: string } =>
  resolveLookup(name, getEpiStatuses());
const resolveSpeciesByName = (name: string): { id: number; text: string } =>
  resolveLookup(name, getSpecies());
const resolveVaccinationStatusByName = (
  name: string
): { id: number; text: string } =>
  resolveLookup(name, getVaccinationStatuses());
const resolveAnimalStatusByName = (
  name: string
): { id: number; text: string } | null => {
  if (!name || typeof name !== "string") return null;
  const found = getAnimalStatuses().find(
    (s) => s.text.toLowerCase() === name.toLowerCase()
  );
  return found ? { id: found.id, text: found.text } : null;
};

const resolveStateByAbbreviation = (abbr: string): number | null => {
  if (!abbr || typeof abbr !== "string") return null;
  const found = getStates().find(
    (s) => s.abbreviation?.toLowerCase() === abbr.toLowerCase()
  );
  return found?.id || null;
};

const resolveCountyByName = (name: string): number | null => {
  if (!name || typeof name !== "string") return null;
  const found = getCounties().find(
    (c) => c.name?.toLowerCase() === name.toLowerCase()
  );
  return found?.id || null;
};

// =============================================================================
// CONVERSION HELPERS (single API → UI conversion point)
// =============================================================================

/**
 * Convert API case data to UI format (CaseFormInput)
 * Dates are converted to YYYY-MM-DD format for form inputs
 * Extracts .text from API lookup objects directly (no store lookup needed)
 */
const convertCaseApiToUi = (caseData: any) => ({
  caseId: caseData.id,
  incidentId: caseData.incidentId,
  caseEpiStatus:
    typeof caseData.epiStatus === "string"
      ? caseData.epiStatus
      : caseData.epiStatus?.text || "",
  caseSpecies:
    typeof caseData.species === "string"
      ? caseData.species
      : caseData.species?.text || "",
  caseVaccinationStatus:
    typeof caseData.vaccinationStatus === "string"
      ? caseData.vaccinationStatus
      : caseData.vaccinationStatus?.text || "",
  caseAnimalStatus:
    typeof caseData.animalStatus === "string"
      ? caseData.animalStatus
      : caseData.animalStatus?.text || "",
  caseBreed: caseData.breed || "",
  caseAgeInYears: caseData.ageInYears ?? null,
  caseNumber: caseData.caseNumber || "",
  caseLabNumber: caseData.labNumber || "",
  caseNote: caseData.note || "",
  caseIsNotified: caseData.isNotified ?? false,
  caseDateOnset: isoToDateOnly(caseData.dateOnset),
  caseDateReported: isoToDateOnly(caseData.dateReported),
  caseDateDied: isoToDateOnly(caseData.dateDied),
  caseActive: caseData.active !== false,
});

interface VeterinaryState {
  incidents: IncidentRecord[];
  caseSummaries: CaseIndexRecord[];
  loading: boolean;
  error?: string | null;
  loadAll: () => Promise<void>;
  loadCaseSummaries: () => Promise<void>;
  loadIncidentDetails: (incidentId: number) => Promise<IncidentRecord | null>;
  add: (input: IncidentFormInput) => Promise<IncidentRecord>;
  update: (id: number, input: IncidentFormInput) => Promise<IncidentRecord>;
  updateIncidentWithCases: (
    id: number,
    incidentData: any,
    casesData: any[],
    options?: {
      onProgress?: (current: number, total: number) => void;
      incidentIsDirty?: boolean;
      dirtyCaseIndices?: number[];
    }
  ) => Promise<IncidentRecord | null>;
  getById: (id: number) => IncidentRecord | undefined;
}

export const useVeterinaryStore = create<VeterinaryState>()(
  devtools((set, get) => ({
    incidents: [],
    caseSummaries: [],
    loading: false,
    error: null,
    loadAll: async () => {
      set({ loading: true, error: null });

      try {
        if (!apiClient) {
          throw new Error("API client not available");
        }

        const apiData = await apiClient.authenticatedRequest(async () => {
          const response = await apiClient.api("api/veterinary").get();
          return response.content;
        });

        const incidentsArray = Array.isArray(apiData)
          ? apiData
          : apiData?.results || apiData?.data || apiData?.incidents || [];

        const transformedIncidents: IncidentRecord[] = incidentsArray.map(
          (incident: any) => ({
            incidentId: incident.id,
            incidentActive: incident.active !== false,
            incidentStreetAddress: incident.streetAddress || "Unknown Location",
            incidentCity: incident.city || "",
            incidentState:
              typeof incident.state === "object"
                ? incident.state?.abbreviation || ""
                : incident.state || "",
            incidentZip: incident.zip || "",
            incidentCounty:
              typeof incident.county === "object"
                ? incident.county?.name || ""
                : incident.countyName || incident.county || "",
            incidentIsAddressValidated: incident.isAddressValidated || false,
            incidentLatitude: incident.latitude || "",
            incidentLongitude: incident.longitude || "",
            incidentGpsNote: incident.gpsNote || "",
            incidentOwnerName: incident.ownerName || "",
            incidentOwnerPhone: incident.ownerPhone || "",
            incidentOwnerEmail: incident.ownerEmail || "",
            incidentOwnerStreetAddress: incident.ownerStreetAddress || "",
            incidentOwnerCity: incident.ownerCity || "",
            incidentOwnerState:
              typeof incident.ownerState === "object"
                ? incident.ownerState?.abbreviation || ""
                : incident.ownerState || "",
            incidentOwnerZip: incident.ownerZip || "",
            incidentOwnerCounty:
              typeof incident.ownerCounty === "object"
                ? incident.ownerCounty?.name || ""
                : incident.ownerCounty || "",
            incidentIsOwnerAddressValidated:
              incident.isOwnerAddressValidated || false,
            incidentOwnerLatitude: incident.ownerLatitude || "",
            incidentOwnerLongitude: incident.ownerLongitude || "",
            incidentOwnerGpsNote: incident.ownerGpsNote || "",
            incidentComment: incident.comment || "",
            createdUtc: incident.createdUtc || new Date().toISOString(),
            updatedUtc: incident.updatedUtc || new Date().toISOString(),
          })
        );

        set({
          incidents: transformedIncidents,
          error: null,
          loading: false,
        });
      } catch (apiError: any) {
        set({
          error: `Failed to load data: ${apiError.message}`,
          loading: false,
        });
      }
    },
    loadCaseSummaries: async () => {
      set({ loading: true, error: null });

      try {
        if (!apiClient) {
          throw new Error("API client not available");
        }

        const apiData = await apiClient.authenticatedRequest(async () => {
          const response = await apiClient.api("api/veterinary").get();
          return response.content;
        });

        const casesArray = Array.isArray(apiData)
          ? apiData
          : apiData?.results || apiData?.data || apiData?.cases || [];

        const caseSummaries: CaseIndexRecord[] = casesArray.map(
          (caseData: any) => ({
            caseId: caseData.caseId,
            incidentId: caseData.incidentId || caseData.id, // Use incidentId if available, fall back to id
            incidentStreetAddress: caseData.streetAddress,
            incidentCity: caseData.city || "Unknown",
            incidentCountyName: caseData.countyName || "Unknown",
            caseDateOnset: caseData.dateOnset || "",
            caseDateReported: caseData.dateReported || "",
          })
        );

        set({ caseSummaries, loading: false, error: null });
      } catch (apiError: any) {
        set({
          error: `Failed to load case summaries: ${apiError.message}`,
          loading: false,
        });
      }
    },
    add: async (input) => {
      if (!apiClient) {
        throw new Error("API client not available");
      }

      try {
        // Transform cases data
        const transformedCases = ((input as any).cases || []).map(
          (caseItem: any) => {
            const transformedCase: any = {
              incidentId: 0, // Will be set by API
              breed: caseItem.caseBreed || null,
              ageInYears: caseItem.caseAgeInYears ?? null,
              caseNumber: caseItem.caseNumber || null,
              labNumber: caseItem.caseLabNumber || null,
              note: caseItem.caseNote || null,
              isNotified: caseItem.caseIsNotified ?? false,
            };

            // Only include lookup IDs if they have values
            if (
              caseItem.caseEpiStatus &&
              caseItem.caseEpiStatus.trim() !== ""
            ) {
              transformedCase.epiStatusId = resolveEpiStatusByName(
                caseItem.caseEpiStatus
              ).id;
            }
            if (caseItem.caseSpecies && caseItem.caseSpecies.trim() !== "") {
              transformedCase.speciesId = resolveSpeciesByName(
                caseItem.caseSpecies
              ).id;
            }
            if (
              caseItem.caseVaccinationStatus &&
              caseItem.caseVaccinationStatus.trim() !== ""
            ) {
              transformedCase.vaccinationStatusId =
                resolveVaccinationStatusByName(
                  caseItem.caseVaccinationStatus
                ).id;
            }

            if (
              caseItem.caseAnimalStatus &&
              caseItem.caseAnimalStatus.trim() !== ""
            ) {
              const resolved = resolveAnimalStatusByName(
                caseItem.caseAnimalStatus
              );
              if (resolved) {
                transformedCase.animalStatusId = resolved.id;
              }
            }

            // Handle dates
            if (
              caseItem.caseDateOnset &&
              caseItem.caseDateOnset.trim() !== ""
            ) {
              transformedCase.dateOnset = new Date(
                caseItem.caseDateOnset
              ).toISOString();
            } else {
              transformedCase.dateOnset = new Date().toISOString();
            }

            if (
              caseItem.caseDateReported &&
              caseItem.caseDateReported.trim() !== ""
            ) {
              transformedCase.dateReported = new Date(
                caseItem.caseDateReported
              ).toISOString();
            } else {
              transformedCase.dateReported = new Date().toISOString();
            }

            // AddPage uses "caseDateDied" field name
            if (caseItem.caseDateDied && caseItem.caseDateDied.trim() !== "") {
              transformedCase.dateDied = new Date(
                caseItem.caseDateDied
              ).toISOString();
            } else {
              transformedCase.dateDied = null;
            }

            return transformedCase;
          }
        );

        const apiResult = await apiClient.authenticatedRequest(async () => {
          const payload = {
            active: input.incidentActive !== false,
            streetAddress: input.incidentStreetAddress || null,
            city: input.incidentCity || null,
            stateId: resolveStateByAbbreviation(input.incidentState),
            countyId: resolveCountyByName(input.incidentCounty),
            zip: input.incidentZip || null,
            latitude: input.incidentLatitude
              ? String(input.incidentLatitude)
              : null,
            longitude: input.incidentLongitude
              ? String(input.incidentLongitude)
              : null,
            gpsNote: input.incidentGpsNote || null,
            isAddressValidated: input.incidentIsAddressValidated ?? false,
            ownerName: input.incidentOwnerName || null,
            ownerPhone: input.incidentOwnerPhone || null,
            ownerEmail: input.incidentOwnerEmail || null,
            ownerStreetAddress: input.incidentOwnerStreetAddress || null,
            ownerCity: input.incidentOwnerCity || null,
            ownerStateId: resolveStateByAbbreviation(input.incidentOwnerState),
            ownerCountyId: resolveCountyByName(input.incidentOwnerCounty),
            ownerZip: input.incidentOwnerZip || null,
            ownerLatitude: input.incidentOwnerLatitude
              ? String(input.incidentOwnerLatitude)
              : null,
            ownerLongitude: input.incidentOwnerLongitude
              ? String(input.incidentOwnerLongitude)
              : null,
            ownerGpsNote: input.incidentOwnerGpsNote || null,
            ownerIsAddressValidated:
              input.incidentIsOwnerAddressValidated ?? false,
            comment: input.incidentComment || null,
            cases: transformedCases,
          };

          const response = await apiClient.api("api/veterinary").post(payload);
          return response?.content || response;
        });

        // Build complete record from form input and API response using shared conversion
        const rec: any = {
          incidentId: apiResult.id,
          incidentActive: apiResult.active !== false,
          incidentStreetAddress: input.incidentStreetAddress || "",
          incidentCounty: input.incidentCounty || "",
          incidentCity: input.incidentCity || "",
          incidentState: input.incidentState || "",
          incidentZip: input.incidentZip || "",
          incidentLatitude: input.incidentLatitude || "",
          incidentLongitude: input.incidentLongitude || "",
          incidentGpsNote: input.incidentGpsNote || "",
          incidentIsAddressValidated: input.incidentIsAddressValidated ?? false,
          incidentOwnerName: input.incidentOwnerName || "",
          incidentOwnerPhone: input.incidentOwnerPhone || "",
          incidentOwnerEmail: input.incidentOwnerEmail || "",
          incidentOwnerStreetAddress: input.incidentOwnerStreetAddress || "",
          incidentOwnerCity: input.incidentOwnerCity || "",
          incidentOwnerState: input.incidentOwnerState || "",
          incidentOwnerZip: input.incidentOwnerZip || "",
          incidentOwnerCounty: input.incidentOwnerCounty || "",
          incidentOwnerLatitude: input.incidentOwnerLatitude || "",
          incidentOwnerLongitude: input.incidentOwnerLongitude || "",
          incidentOwnerGpsNote: input.incidentOwnerGpsNote || "",
          incidentIsOwnerAddressValidated:
            input.incidentIsOwnerAddressValidated ?? false,
          incidentComment: input.incidentComment || "",
          createdUtc: apiResult.createdUtc || new Date().toISOString(),
          updatedUtc: apiResult.updatedUtc || new Date().toISOString(),
          // API → UI conversion using shared helper
          cases: (apiResult.cases || []).map((caseData: any, idx: number) => {
            const inputCase = ((input as any).cases || [])[idx] || {};
            const converted = convertCaseApiToUi(caseData);
            // Preserve form input note if not returned by API
            return {
              ...converted,
              caseNote:
                converted.caseNote ||
                inputCase.note ||
                inputCase.caseNote ||
                "",
            };
          }),
        };

        set((s) => ({ incidents: [...s.incidents, rec] }));
        return rec;
      } catch (apiError) {
        console.error("Veterinary API add failed:", apiError);
        throw apiError;
      }
    },
    update: async (id, input) => {
      if (!apiClient) {
        throw new Error("API client not available");
      }

      try {
        await apiClient.authenticatedRequest(async () => {
          return await apiClient.patch(`/api/veterinary/${id}`, {
            id: id,
            streetAddress: input.incidentStreetAddress || null,
            city: input.incidentCity || "Unknown",
            stateId: resolveStateByAbbreviation(input.incidentState),
            countyId: resolveCountyByName(input.incidentCounty),
            zip: input.incidentZip || "00000",
            latitude: input.incidentLatitude
              ? String(input.incidentLatitude)
              : null,
            longitude: input.incidentLongitude
              ? String(input.incidentLongitude)
              : null,
            gpsNote: input.incidentGpsNote || null,
            isAddressValidated: input.incidentIsAddressValidated ?? false,
            ownerName: input.incidentOwnerName || null,
            ownerPhone: input.incidentOwnerPhone || null,
            ownerStreetAddress: input.incidentOwnerStreetAddress || null,
            ownerCity: input.incidentOwnerCity || null,
            ownerStateId:
              resolveStateByAbbreviation(input.incidentOwnerState) || 12,
            ownerCountyId: resolveCountyByName(input.incidentOwnerCounty) || 1,
            ownerZip: input.incidentOwnerZip || null,
            ownerLatitude: input.incidentOwnerLatitude
              ? String(input.incidentOwnerLatitude)
              : null,
            ownerLongitude: input.incidentOwnerLongitude
              ? String(input.incidentOwnerLongitude)
              : null,
            ownerGpsNote: input.incidentOwnerGpsNote || null,
            ownerIsAddressValidated:
              input.incidentIsOwnerAddressValidated ?? false,
          });
        });

        let updated: IncidentRecord | undefined;
        set((s) => ({
          incidents: s.incidents.map((x) =>
            x.incidentId === id
              ? (updated = {
                  ...x,
                  ...input,
                  updatedUtc: new Date().toISOString(),
                })
              : x
          ),
        }));

        if (!updated) throw new Error("Not found");
        return updated;
      } catch (apiError) {
        console.error("Veterinary API update failed:", apiError);
        throw apiError;
      }
    },

    loadIncidentDetails: async (incidentId: number) => {
      try {
        if (!apiClient) {
          throw new Error("API client not available");
        }

        const incidentData = await apiClient.authenticatedRequest(async () => {
          const response = await apiClient
            .api(`api/veterinary/${incidentId}`)
            .get();
          return response?.content || response;
        });

        // Transform API response to IncidentRecord using shared conversion helper
        const record: any = {
          incidentId: incidentData.id,
          incidentActive: incidentData.active !== false,
          incidentStreetAddress:
            incidentData.streetAddress || incidentData.address || "",
          incidentCounty:
            typeof incidentData.county === "object"
              ? incidentData.county?.name || ""
              : incidentData.county || "Unknown",
          incidentCity: incidentData.city || null,
          incidentState:
            typeof incidentData.state === "object"
              ? incidentData.state?.abbreviation || ""
              : incidentData.state || "",
          incidentZip: incidentData.zip || "",
          incidentIsAddressValidated: incidentData.isAddressValidated || false,
          incidentLatitude: incidentData.latitude || "",
          incidentLongitude: incidentData.longitude || "",
          incidentGpsNote: incidentData.gpsNote || "",
          incidentOwnerName: incidentData.ownerName || "",
          incidentOwnerPhone:
            incidentData.ownerPhone || incidentData.ownerNumber || "",
          incidentOwnerEmail: incidentData.ownerEmail || "",
          incidentOwnerStreetAddress:
            incidentData.ownerStreetAddress || incidentData.ownerAddress || "",
          incidentOwnerCity: incidentData.ownerCity || "",
          incidentOwnerState:
            typeof incidentData.ownerState === "object"
              ? incidentData.ownerState?.abbreviation || ""
              : incidentData.ownerState || "",
          incidentOwnerZip: incidentData.ownerZip || "",
          incidentOwnerCounty:
            typeof incidentData.ownerCounty === "object"
              ? incidentData.ownerCounty?.name || ""
              : incidentData.ownerCounty || "",
          incidentIsOwnerAddressValidated:
            incidentData.isOwnerAddressValidated ||
            incidentData.ownerIsAddressValidated ||
            false,
          incidentOwnerLatitude: incidentData.ownerLatitude || "",
          incidentOwnerLongitude: incidentData.ownerLongitude || "",
          incidentOwnerGpsNote: incidentData.ownerGpsNote || "",
          incidentComment: incidentData.comment || "",
          createdUtc: incidentData.createdUtc || new Date().toISOString(),
          updatedUtc: incidentData.updatedUtc || new Date().toISOString(),
          // API → UI conversion using shared helper
          cases: (incidentData.cases || []).map(convertCaseApiToUi),
        };

        // Update the specific incident in the store
        set((state) => {
          const incidentIndex = state.incidents.findIndex(
            (inc) => inc.incidentId === incidentId
          );

          if (incidentIndex >= 0) {
            // Incident exists, update it
            return {
              incidents: state.incidents.map((inc) =>
                inc.incidentId === incidentId ? record : inc
              ),
            };
          } else {
            // Incident doesn't exist, add it
            return {
              incidents: [...state.incidents, record],
            };
          }
        });

        return record;
      } catch (error) {
        console.error(`❌ Error loading incident ${incidentId}:`, error);
        return null;
      }
    },

    updateIncidentWithCases: async (id, incidentData, casesData, options) => {
      const {
        onProgress,
        incidentIsDirty = true,
        dirtyCaseIndices,
      } = options || {};
      if (!apiClient) {
        throw new Error("API client not available");
      }

      // Filter cases to only dirty ones if dirtyCaseIndices is provided
      const casesToUpdate =
        dirtyCaseIndices !== undefined
          ? casesData.filter((_, index) => dirtyCaseIndices.includes(index))
          : casesData;

      // Calculate total operations: incident (if dirty) + cases
      const totalOperations = (incidentIsDirty ? 1 : 0) + casesToUpdate.length;
      let completedOperations = 0;

      // Helper to update progress
      const updateProgress = () => {
        completedOperations++;
        if (onProgress) {
          onProgress(completedOperations, totalOperations);
        }
      };

      try {
        // Update incident details only if dirty
        if (incidentIsDirty) {
          // Transform incident data to API format
          const transformedIncidentData = {
            id: id,
            active: incidentData.incidentActive !== false,
            streetAddress: incidentData.incidentStreetAddress || null,
            city: incidentData.incidentCity || null,
            stateId:
              typeof incidentData.incidentState === "object"
                ? incidentData.incidentState?.id
                : resolveStateByAbbreviation(incidentData.incidentState),
            countyId:
              typeof incidentData.incidentCounty === "object"
                ? incidentData.incidentCounty?.id
                : resolveCountyByName(incidentData.incidentCounty),
            zip: incidentData.incidentZip || null,
            latitude: incidentData.incidentLatitude
              ? String(incidentData.incidentLatitude)
              : null,
            longitude: incidentData.incidentLongitude
              ? String(incidentData.incidentLongitude)
              : null,
            gpsNote: incidentData.incidentGpsNote || null,
            isAddressValidated:
              incidentData.incidentIsAddressValidated ?? false,
            ownerName: incidentData.incidentOwnerName || null,
            ownerPhone: incidentData.incidentOwnerPhone || null,
            ownerEmail: incidentData.incidentOwnerEmail || null,
            ownerStreetAddress: incidentData.incidentOwnerStreetAddress || null,
            ownerCity: incidentData.incidentOwnerCity || null,
            ownerStateId:
              typeof incidentData.incidentOwnerState === "object"
                ? incidentData.incidentOwnerState?.id
                : resolveStateByAbbreviation(incidentData.incidentOwnerState),
            ownerCountyId:
              typeof incidentData.incidentOwnerCounty === "object"
                ? incidentData.incidentOwnerCounty?.id
                : resolveCountyByName(incidentData.incidentOwnerCounty),
            ownerZip: incidentData.incidentOwnerZip || null,
            ownerLatitude: incidentData.incidentOwnerLatitude
              ? String(incidentData.incidentOwnerLatitude)
              : null,
            ownerLongitude: incidentData.incidentOwnerLongitude
              ? String(incidentData.incidentOwnerLongitude)
              : null,
            ownerGpsNote: incidentData.incidentOwnerGpsNote || null,
            ownerIsAddressValidated:
              incidentData.incidentIsOwnerAddressValidated ?? false,
            comment: incidentData.incidentComment || null,
          };

          try {
            await apiClient.authenticatedRequest(async () => {
              const response = await apiClient
                .api(`api/veterinary/${id}`)
                .request("PATCH", transformedIncidentData);

              if (!response.ok) {
                throw response;
              }

              // Response content may be undefined for empty 200 responses
              return response.content || null;
            });
            updateProgress();
          } catch (error: any) {
            // Ignore JSON parse errors for successful empty responses
            if (
              error.message &&
              error.message.includes("Unexpected end of JSON input")
            ) {
              // Empty response is OK for PATCH
              updateProgress();
            } else {
              throw error;
            }
          }
        } // Update or create each case
        for (const caseItem of casesToUpdate) {
          // Transform lookup text values to IDs
          // Exclude incidentId for new cases - for POST it comes from URL
          // Also extract caseActive to rename it to 'active' for the API
          // Extract caseNote to rename it to 'note' for the API
          const {
            caseId,
            caseEpiStatus,
            caseSpecies,
            caseVaccinationStatus,
            caseAnimalStatus,
            incidentId: _incidentId,
            caseActive,
            caseNote,
            caseBreed,
            caseAgeInYears,
            caseNumber,
            caseLabNumber,
            caseDateOnset,
            caseDateReported,
            caseDateDied,
            caseIsNotified: _caseIsNotified,
          } = caseItem;

          // Build base transformed case - explicitly list fields to avoid sending unexpected properties
          const transformedCase: any = {
            active: caseActive ?? true,
            note: caseNote || "",
            breed: caseBreed || null,
            ageInYears: caseAgeInYears ?? null,
            caseNumber: caseNumber || null,
            labNumber: caseLabNumber || null,
            // Note: isNotified is not part of UpdateCaseInput schema, only AddCaseInput
          };

          // Only set lookup IDs if values are provided (not empty)
          if (caseEpiStatus && caseEpiStatus.trim() !== "") {
            transformedCase.epiStatusId =
              resolveEpiStatusByName(caseEpiStatus).id;
          }
          if (caseSpecies && caseSpecies.trim() !== "") {
            transformedCase.speciesId = resolveSpeciesByName(caseSpecies).id;
          }
          if (caseVaccinationStatus && caseVaccinationStatus.trim() !== "") {
            transformedCase.vaccinationStatusId =
              resolveVaccinationStatusByName(caseVaccinationStatus).id;
          }
          if (caseAnimalStatus && caseAnimalStatus.trim() !== "") {
            const resolved = resolveAnimalStatusByName(caseAnimalStatus);
            if (resolved) {
              transformedCase.animalStatusId = resolved.id;
            }
          }

          // Handle dates - dateOnset and dateReported default to today if empty, dateDied is nullable
          // Note: API uses dateOnset, dateReported, dateDied
          if (caseDateOnset && caseDateOnset.trim() !== "") {
            transformedCase.dateOnset = new Date(caseDateOnset).toISOString();
          } else {
            transformedCase.dateOnset = new Date().toISOString();
          }

          if (caseDateReported && caseDateReported.trim() !== "") {
            transformedCase.dateReported = new Date(
              caseDateReported
            ).toISOString();
          } else {
            transformedCase.dateReported = new Date().toISOString();
          }

          if (caseDateDied && caseDateDied.trim() !== "") {
            transformedCase.dateDied = new Date(caseDateDied).toISOString();
          } else {
            transformedCase.dateDied = null;
          }

          // Remove the frontend field names that don't match the API (in case they exist)
          delete transformedCase.dateOfOnset;
          delete transformedCase.dateOfDeath;

          if (caseId) {
            // Update existing case - PATCH: api/veterinary/cases/${caseId}
            // Build payload matching UpdateCaseInput schema exactly
            const patchPayload = {
              id: caseId, // Required by UpdateCaseInput
              incidentId: caseItem.incidentId || id, // Required by UpdateCaseInput
              ...transformedCase,
            };
            try {
              await apiClient.authenticatedRequest(async () => {
                const response = await apiClient
                  .api(`api/veterinary/cases/${caseId}`)
                  .request("PATCH", patchPayload);

                if (!response.ok) {
                  throw response;
                }

                // Response content may be undefined for empty 200 responses
                return response.content || null;
              });
            } catch (error: any) {
              // Ignore JSON parse errors for successful empty responses
              if (
                error.message &&
                error.message.includes("Unexpected end of JSON input")
              ) {
                // Empty response is OK for PATCH
              } else {
                throw error;
              }
            }
          } else {
            // Create new case - POST: api/veterinary/${incidentId}/cases
            // Build explicit payload matching AddCaseInput schema (no id, no isNotified, no incidentId)
            const addCasePayload = {
              note: transformedCase.note || "",
              active: transformedCase.active ?? true,
              epiStatusId: transformedCase.epiStatusId,
              speciesId: transformedCase.speciesId,
              breed: transformedCase.breed || "",
              dateOnset: transformedCase.dateOnset,
              dateReported: transformedCase.dateReported,
              dateDied: transformedCase.dateDied,
              caseNumber: transformedCase.caseNumber || "",
              animalStatusId: transformedCase.animalStatusId,
              vaccinationStatusId: transformedCase.vaccinationStatusId,
              ageInYears: transformedCase.ageInYears,
              labNumber: transformedCase.labNumber || "",
            };

            await apiClient.authenticatedRequest(async () => {
              const apiResponse = await apiClient
                .api(`api/veterinary/${id}/cases`)
                .post(addCasePayload);
              return apiResponse?.content || apiResponse;
            });
          }
          updateProgress();
        }

        // Reload the incident to sync with database state
        // This ensures UI reflects the latest data after updates
        return await get().loadIncidentDetails(id);
      } catch (apiError) {
        throw apiError;
      }
    },

    getById: (id) => get().incidents.find((x) => x.incidentId === id),
  }))
);
